/**
 * Typed service wrappers for the online-ordering API.
 *
 * Endpoint paths are mirrored from the v1 Angular SDK at
 * `online-ordering/src/app/api/online-ordering-service/api/*.service.ts`.
 * Models come from the parallel-generated TypeScript types under
 * `@/api/online-ordering-service/model` and `@/api/catalog-service/model`.
 */

import {
  apiFetch,
  buildUrl,
  configureAuthRefresh,
  getRefreshToken,
  setRefreshToken,
  clearRefreshToken,
  API_BASE,
  getToken,
  ApiError,
} from "./http";

// The backend now returns the refresh token in the body (localStorage scheme).
type TokenResponseWithRefresh = TokenResponse & { refreshToken?: string };
import { normalizeCountryCode } from "@/lib/location";
import { readParticipant } from "@/lib/participant-token";
import type { CustomerConsent, ConsentPatch } from "@/api/consent-types";
import { isPromotionWithinWindow } from "@/lib/promotion-window";
import { meetsRequiredItemCriteria } from "@/lib/promotion-eligibility";

import type {
  TokenResponse,
  Customer,
  CustomerCreate,
  CustomerPatch,
  AppSchemasCustomerAddress as CustomerAddress,
  AddressCreate,
  AddressUpdate,
  OrderInput,
  OrderLineInput,
  OrderOutput,
  AvailabilityBlockResponse,
  PromotionListResponse,
  Promotion,
  OnlineOrderingConfig,
  PaymentIntentCreateRequest,
  PaymentIntentResponse,
  ChargeCreate,
  ResponseProcessOnlinePaymentV1OnlineOrderingPaymentsOnlinePost as ChargeProcessResponse,
  NearestBranchResponse,
  QuoteEtaRequest,
  QuoteEtaResponse,
  PriceQuoteRequest,
  PriceQuote,
} from "@/api/online-ordering-service/model";

import type { MenuRenderResponse } from "@/api/catalog-service/model";
import type { PromotionReward } from "@/api/online-ordering-service/model/promotionReward";
import type { SurchargeDefinition, TaxRef } from "@/lib/surcharges";

import {
  evaluateReward,
  type CalcContext,
  type Reward,
  type CategoryBundle,
  type DiscountTarget,
  type LoyaltySignals,
  type UnitSelection,
  type RewardKind,
} from "@/lib/promotion-engine";

function normalizeAddressPayload<T extends AddressCreate | AddressUpdate>(
  addr: T,
): T {
  if (!("country" in addr)) return addr;
  return {
    ...addr,
    country: normalizeCountryCode(addr.country),
  } as T;
}

/* ──────── Auth ──────── */

/**
 * Response from `POST /v1/online-ordering/auth/send-otp`. Production replies
 * with `{}` (200) once the SMS is dispatched. In dev / staging tenants where
 * SMS sending is disabled, the backend returns the templated message body
 * inline as `{response: "SMS Disabled, message: {…, 'messageText': '8217 is
 * your verification number.', …} for +201… to be sent."}` so engineers can
 * read the OTP without provisioning a real SMS provider.
 *
 * The shape is intentionally loose because the backend bakes the dev hint
 * into a free-form string; callers extract the code via `extractDevOtp()`.
 */
export type SendOtpResponse = {
  response?: string | null;
  [key: string]: unknown;
};

export const authApi = {
  /** POST /v1/online-ordering/auth/send-otp */
  sendOtp(phone: string): Promise<SendOtpResponse> {
    return apiFetch<SendOtpResponse>("/v1/online-ordering/auth/send-otp", {
      method: "POST",
      body: { phone },
      skipAuth: true,
    });
  },

  /** POST /v1/online-ordering/auth/verify-otp */
  async verifyOtp(phone: string, code: string): Promise<TokenResponse> {
    const res = await apiFetch<any>(
      "/v1/online-ordering/auth/verify-otp",
      { method: "POST", body: { phone, otp: code }, skipAuth: true },
    );
    const rt = res.refreshToken || res.refresh_token;
    if (rt) setRefreshToken(rt);
    return {
      ...res,
      accessToken: res.accessToken || res.access_token,
      token_type: res.token_type || res.tokenType,
    } as TokenResponse;
  },

  /** POST /v1/online-ordering/auth/refresh — replays the stored refresh token
   * via X-Refresh-Token (works on custom domains where the cookie is blocked). */
  async refresh(): Promise<TokenResponse> {
    const rt = getRefreshToken();
    const res = await apiFetch<any>(
      "/v1/online-ordering/auth/refresh",
      { method: "POST", headers: rt ? { "X-Refresh-Token": rt } : undefined },
    );
    const newRt = res.refreshToken || res.refresh_token;
    if (newRt) setRefreshToken(newRt);
    return {
      ...res,
      accessToken: res.accessToken || res.access_token,
      token_type: res.token_type || res.tokenType,
    } as TokenResponse;
  },

  /** POST /v1/online-ordering/auth/logout */
  async logout(): Promise<void> {
    const rt = getRefreshToken();
    try {
      await apiFetch<void>("/v1/online-ordering/auth/logout", {
        method: "POST",
        headers: rt ? { "X-Refresh-Token": rt } : undefined,
      });
    } finally {
      clearRefreshToken();
    }
  },
};

// Hand the refresh impl to http.ts so its 401 recovery path can call it
// without creating a circular import. Skips the standard 401-recovery flow
// itself (refresh endpoint hitting 401 must surface, not loop).
configureAuthRefresh(() => authApi.refresh());

/**
 * Pull a verification code out of a `send-otp` response when the backend is
 * in dev/staging "SMS Disabled" mode. The response payload looks like:
 *
 *   { "response": "SMS Disabled, message: {…, 'recipients': '+201227685199',
 *     'messageText': '8217 is your verification number.', …} for +201… to be
 *     sent." }
 *
 * Naïvely grabbing the first digit run matches the phone number, so we have
 * to anchor on the OTP-specific text first:
 *  1. Isolate the `messageText` value when present and pull digits from it.
 *  2. Fall back to matching `<digits> is your verification` anywhere in the
 *     string (Tappya's canonical OTP template).
 * Returns `null` for production responses (where there is no code to extract).
 */
export function extractDevOtp(
  res: SendOtpResponse | null | undefined,
): string | null {
  const raw = res?.response;
  if (typeof raw !== "string") return null;
  const lower = raw.toLowerCase();
  // Recognize every inline-code marker the backend may emit:
  //  - "SMS Disabled, message: …"            (legacy dev echo, SMS off)
  //  - "SMS not sent (test pathway): …"       (gateway wallet-zero / error fallback)
  // In all of these the actual OTP is carried inline and must be surfaced.
  const isInlineCode =
    lower.includes("sms disabled") ||
    lower.includes("test pathway") ||
    lower.includes("sms not sent");
  if (!isInlineCode) return null;

  // Try to slice out the `messageText` value. The backend serializes a Python
  // dict so the quotes can be either single or double.
  const msgMatch = raw.match(/messageText['"]?\s*:\s*['"]([^'"]+)['"]/);
  if (msgMatch?.[1]) {
    const inMsg = msgMatch[1].match(/\b(\d{4,8})\b/);
    if (inMsg?.[1]) return inMsg[1];
  }

  // Fall back to the canonical "<code> is your verification" phrasing.
  const phraseMatch = raw.match(/\b(\d{4,8})\b\s+is your verification/i);
  if (phraseMatch?.[1]) return phraseMatch[1];

  return null;
}

/* ──────── Menu ──────── */
/**
 * NOTE: The online-ordering service only exposes `/v1/online-ordering/menu-schema`
 * which returns the JSON schema (NOT the rendered menu). The actual menu render
 * lives on the catalog service: `/v1/catalog/menus/{menuId}/render`.
 *
 * The v2 caller passes `branchId` here for ergonomics; we keep the v1
 * online-ordering helpers around for parity but real menu fetching should hit
 * `getMenuRender(menuId, branchId)`.
 */
export const menuApi = {
  /**
   * GET /v1/online-ordering/menu-schema
   * Returns the JSON schema describing the MenuRenderResponse shape.
   * Useful for consumers that want to validate/render dynamically.
   */
  // TODO: tighten return type once a Schema model exists in generated models.
  getMenuSchema(): Promise<unknown> {
    return apiFetch<unknown>("/v1/online-ordering/menu-schema");
  },

  /**
   * GET /v1/catalog/menus/{menuId}/render?branchId=&context=
   * Renders a published menu. v1 used this through the catalog service.
   */
  getMenuRender(
    menuId: string,
    opts: { branchId?: string; context?: string } = {},
  ): Promise<MenuRenderResponse> {
    return apiFetch<MenuRenderResponse>(
      buildUrl(`/v1/catalog/menus/${encodeURIComponent(menuId)}/render`, {
        branchId: opts.branchId,
        context: opts.context,
      }),
    );
  },

  /**
   * Fetch a pre-rendered menu JSON from a public URL — typically the
   * `menuContentUrl` returned by `configApi.get(slug)` (Firebase Storage).
   * v1's `MenuService.loadMenuFromUrl` does the same: a bare GET, no
   * gateway, no auth. Bypasses our `apiFetch` wrapper so we don't add
   * `X-API-KEY` / `Authorization` to a Firebase Storage URL.
   *
   * In dev, Firebase Storage GET responses don't echo
   * `Access-Control-Allow-Origin` (only the OPTIONS preflight does), so
   * the browser blocks them. We route through the Vite proxy at
   * `/firebase-storage/*` (same origin as the dev server) to sidestep
   * CORS entirely. In production the bundle ships from a real origin
   * that should be allow-listed by Firebase Storage rules.
   */
  async fetchFromUrl(url: string): Promise<MenuRenderResponse> {
    const rewritten =
      import.meta.env.DEV &&
      url.startsWith("https://firebasestorage.googleapis.com")
        ? url.replace(
            "https://firebasestorage.googleapis.com",
            "/firebase-storage",
          )
        : url;
    // Cache-bust. The menu JSON lives at a STABLE Storage URL that the catalog
    // service overwrites in place when a merchant republishes. Without this,
    // the browser HTTP cache (Firebase Storage serves the object with a default
    // `Cache-Control: max-age`) keeps returning the OLD menu file across
    // reloads, so menu edits don't show up. A per-call timestamp param makes
    // every request a unique URL → guaranteed fresh fetch; `cache: "no-store"`
    // is belt-and-braces against any intermediary cache. We append on a fresh
    // copy here (not on the URL the hook keys its in-memory cache by) so
    // in-session de-duplication still works.
    const sep = rewritten.includes("?") ? "&" : "?";
    const finalUrl = `${rewritten}${sep}t=${Date.now()}`;
    const res = await fetch(finalUrl, {
      method: "GET",
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      throw new Error(`Menu fetch failed: ${res.status} ${res.statusText}`);
    }
    return (await res.json()) as MenuRenderResponse;
  },

  /**
   * Convenience: fetch the menu for a branch. Currently aliases to
   * `getMenuRender` and requires a `menuId` upstream — left here as the
   * shape v2 callers will want once a "menu by branch" endpoint exists.
   *
   * TODO: once the API exposes a true "menu by branch" public endpoint
   * (e.g. via menu-discovery service), update this wrapper.
   */
  getMenu(
    menuId: string,
    branchId: string,
    channel?: string,
  ): Promise<MenuRenderResponse> {
    return apiFetch<MenuRenderResponse>(
      buildUrl(`/v1/catalog/menus/${encodeURIComponent(menuId)}/render`, {
        branchId,
        context: channel,
      }),
    );
  },

  /**
   * Search menu items. v1's online-ordering SDK does NOT expose a search
   * endpoint (no /menu-schema search), so this is left as a TODO stub that
   * resolves with an empty array. Wire to the actual endpoint when available.
   *
   * TODO: replace with real search endpoint once exposed.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async searchMenu(_branchId: string, _q: string): Promise<unknown[]> {
    return [];
  },
};

/* ──────── Customer ──────── */

export const customerApi = {
  /**
   * GET /v1/online-ordering/customers/me
   *
   * Bootstrap call — fired on app open whenever a token exists. Uses
   * `noAuthPrompt` so a stale-token 401 falls through to the AuthProvider's
   * silent clear-token path instead of slamming the user with a sign-in
   * modal on first paint.
   */
  getMe(): Promise<Customer> {
    return apiFetch<Customer>("/v1/online-ordering/customers/me", {
      noAuthPrompt: true,
    });
  },

  /** PATCH /v1/online-ordering/customers/me */
  updateMe(patch: CustomerPatch): Promise<Customer> {
    return apiFetch<Customer>("/v1/online-ordering/customers/me", {
      method: "PATCH",
      body: patch,
    });
  },

  /** POST /v1/online-ordering/customers */
  // create(body: { name: string }): Promise<Customer> {
  //   return apiFetch<Customer>("/v1/online-ordering/customers", {
  //     method: "POST",
  //     body,

  /**
   * POST /v1/online-ordering/customers
   *
   * Called after OTP verification when `getMe` returns 404 — i.e. this phone
   * has authenticated but no customer record exists yet. Mirrors v1's
   * `customerService.createCustomer({ name })` flow in
   * `online-ordering/src/app/services/customer.service.ts`.
   */
  create(payload: CustomerCreate): Promise<Customer> {
    return apiFetch<Customer>("/v1/online-ordering/customers", {
      method: "POST",
      body: payload,
    });
  },
};

/* ──────── Consent ──────── */

export const consentApi = {
  /** GET /v1/online-ordering/customers/me/consent */
  getMe(): Promise<CustomerConsent> {
    return apiFetch<CustomerConsent>("/v1/online-ordering/customers/me/consent");
  },

  /** POST /v1/online-ordering/customers/me/consent/marketing — single opt-in toggle. */
  optInMarketing(optIn: boolean): Promise<CustomerConsent> {
    return apiFetch<CustomerConsent>(
      "/v1/online-ordering/customers/me/consent/marketing",
      { method: "POST", body: { optIn } },
    );
  },

  /** PATCH /v1/online-ordering/customers/me/consent — preference-center per-channel edit. */
  patch(patch: ConsentPatch): Promise<CustomerConsent> {
    return apiFetch<CustomerConsent>(
      "/v1/online-ordering/customers/me/consent",
      { method: "PATCH", body: patch },
    );
  },
};

/* ──────── Customer Devices (push) ──────── */

export const devicesApi = {
  /** POST /v1/online-ordering/customers/me/devices */
  register(token: string, platform: "web" | "ios" | "android" = "web"): Promise<{ deviceId: string }> {
    return apiFetch<{ deviceId: string }>(
      "/v1/online-ordering/customers/me/devices",
      { method: "POST", body: { token, platform } },
    );
  },

  /** DELETE /v1/online-ordering/customers/me/devices/{deviceId} */
  remove(deviceId: string): Promise<void> {
    return apiFetch<void>(
      `/v1/online-ordering/customers/me/devices/${encodeURIComponent(deviceId)}`,
      { method: "DELETE" },
    );
  },
};

/* ──────── Customer Addresses ──────── */

export const addressesApi = {
  /** GET /v1/online-ordering/customers/me/addresses */
  list(): Promise<CustomerAddress[]> {
    return apiFetch<CustomerAddress[]>(
      "/v1/online-ordering/customers/me/addresses",
    );
  },

  /** GET /v1/online-ordering/customers/me/addresses/{addressId} */
  get(id: string): Promise<CustomerAddress> {
    return apiFetch<CustomerAddress>(
      `/v1/online-ordering/customers/me/addresses/${encodeURIComponent(id)}`,
    );
  },

  /** POST /v1/online-ordering/customers/me/addresses */
  create(addr: AddressCreate): Promise<CustomerAddress> {
    return apiFetch<CustomerAddress>(
      "/v1/online-ordering/customers/me/addresses",
      { method: "POST", body: normalizeAddressPayload(addr) },
    );
  },

  /** PUT /v1/online-ordering/customers/me/addresses/{addressId} */
  update(id: string, addr: AddressUpdate): Promise<CustomerAddress> {
    return apiFetch<CustomerAddress>(
      `/v1/online-ordering/customers/me/addresses/${encodeURIComponent(id)}`,
      { method: "PUT", body: normalizeAddressPayload(addr) },
    );
  },

  /** DELETE /v1/online-ordering/customers/me/addresses/{addressId} */
  delete(id: string): Promise<void> {
    return apiFetch<void>(
      `/v1/online-ordering/customers/me/addresses/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );
  },
};

/* ──────── Order ──────── */

/**
 * Loose payment-intent shape returned from the draft-order endpoint when
 * online payment is required. Matches what the provider SDKs (Paymob/Fawry/
 * Stripe) need to launch their flow. Defined locally to avoid coupling to a
 * generated model that may not yet exist in the SDK.
 */
export type PaymentIntent = PaymentIntentResponse & {
  /**
   * Some backend responses expose an intent id, while the generated
   * PaymentIntentResponse only models transactionRef/clientSecret. Keep this
   * optional and fall back to transactionRef/clientSecret where needed.
   */
  id?: string;
  metadata?: Record<string, unknown> | null;
  returnUrl?: string | null;
};

export type PaymentIntentCreatePayload = PaymentIntentCreateRequest & {
  savedCardId?: string | null;
  returnUrl?: string | null;
  redirectUrl?: string | null;
};

export type OrderCreateResponse = {
  order: OrderOutput;
  intent?: PaymentIntent;
};

export type OrderHistoryResponse = {
  data: OrderOutput[];
  total: number;
  nextCursor?: string | null;
  hasMore?: boolean;
};

export type ConfirmPaymentRequest = {
  intentId?: string | null;
  transactionRef: string;
  providerKey?: string | null;
  raw?: Record<string, unknown>;
};

/**
 * A Mode A queued order carries its identifier as `orderId`, not `id`.
 *
 * When the branch's POS has a device attached, the BFF answers a create with
 * `{ order: { queued: true, queueId, orderId, status }, intent }` instead of a
 * full OrderOutput. Everything downstream reads `order.id` — checkout's
 * navigation, the loyalty hold, the success screen's Track button (which is
 * disabled on an empty id), the payment-intent guard — so leaving the envelope
 * as-is silently breaks all of them for exactly the branches that have a POS.
 * Alias it to `id` here, at the single place every create response passes
 * through, rather than teaching each caller about the queued shape.
 */
type QueuedOrderEnvelope = {
  queued?: boolean;
  queueId?: string;
  orderId?: string;
  status?: string;
};

function normalizeQueuedOrder(order: OrderOutput): OrderOutput {
  const queued = order as OrderOutput & QueuedOrderEnvelope;
  if (order?.id || !queued.orderId) return order;
  return { ...order, id: queued.orderId } as OrderOutput;
}

function unwrapOrderCreateResponse(
  res: OrderOutput | OrderCreateResponse,
): OrderCreateResponse {
  if (
    res &&
    typeof res === "object" &&
    "order" in res &&
    (res as OrderCreateResponse).order
  ) {
    const wrapped = res as OrderCreateResponse;
    return { ...wrapped, order: normalizeQueuedOrder(wrapped.order) };
  }
  const order = normalizeQueuedOrder(res as OrderOutput);
  return {
    order,
    intent:
      (order as { paymentIntent?: PaymentIntent | null }).paymentIntent ??
      undefined,
  };
}

export const orderApi = {
  /**
   * POST /v1/online-ordering/orders
   *
   * Backend now returns `{ order, intent? }` for the draft-order + intent
   * pattern: when online payment is requested in the order's `payment` block,
   * the response includes a `PaymentIntent` ready to hand to a provider SDK.
   * For COD / no payment method, only `order` is returned.
   *
   * For backwards compatibility we also accept a bare `OrderOutput` and wrap
   * it into `{ order }`.
   */
  async create(
    order: OrderInput,
    idempotencyKey?: string,
  ): Promise<OrderCreateResponse> {
    const headers: Record<string, string> = {};
    if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;
    const res = await apiFetch<OrderOutput | OrderCreateResponse>(
      "/v1/online-ordering/orders",
      { method: "POST", body: order, headers },
    );
    return unwrapOrderCreateResponse(res);
  },

  /** GET /v1/online-ordering/orders/{orderId}
   *
   * Sends a participant token alongside the customer JWT whenever one is
   * stored in this tab — that's how group-cart guests (who don't have a
   * Tappya account) load their dine-in order after Fire-to-kitchen. The
   * backend tries the participant token first and falls back to the
   * customer JWT, so this stays correct for normal account holders too.
   */
  get(id: string): Promise<OrderOutput> {
    const stored = readParticipant();
    const headers: Record<string, string> = {};
    if (stored?.participantToken) {
      headers["X-Participant-Token"] = `Bearer ${stored.participantToken}`;
    }
    return apiFetch<OrderOutput>(
      `/v1/online-ordering/orders/${encodeURIComponent(id)}`,
      { headers },
    );
  },

  /** POST /v1/online-ordering/orders/{orderId}/lines */
  addLine(id: string, line: OrderLineInput): Promise<OrderOutput> {
    const stored = readParticipant();
    const headers: Record<string, string> = {};
    if (stored?.participantToken) {
      headers["X-Participant-Token"] = `Bearer ${stored.participantToken}`;
    }
    return apiFetch<OrderOutput>(
      `/v1/online-ordering/orders/${encodeURIComponent(id)}/lines`,
      { method: "POST", body: line, headers },
    );
  },

  /**
   * GET /v1/online-ordering/orders/history
   * Backend returns `{ data: OrderOutput[], total?, cursor?, hasMore? }`.
   */
  async listMine(
    opts: { cursor?: string; limit?: number } = {},
  ): Promise<OrderHistoryResponse> {
    const res = await apiFetch<any>(
      buildUrl("/v1/online-ordering/orders/history", {
        cursor: opts.cursor,
        limit: opts.limit,
      }),
    );
    if (Array.isArray(res)) {
      return { data: res, total: res.length };
    }
    const data = res?.data || res?.orders || [];
    const next = res?.nextCursor || res?.links?.next || null;
    return {
      data,
      total: typeof res?.total === "number" ? res.total : data.length,
      nextCursor: next,
      hasMore: !!next || !!res?.hasMore,
    };
  },

  /**
   * GET /v1/online-ordering/orders/{orderId}/status
   * TODO: tighten with the OrderStatus model from the SDK.
   */
  getStatus(id: string): Promise<unknown> {
    const stored = readParticipant();
    const headers: Record<string, string> = {};
    if (stored?.participantToken) {
      headers["X-Participant-Token"] = `Bearer ${stored.participantToken}`;
    }
    return apiFetch<unknown>(
      `/v1/online-ordering/orders/${encodeURIComponent(id)}/status`,
      { headers },
    );
  },

  /** PATCH /v1/online-ordering/orders/{id}/cancel?orderType={orderType} */
  cancel(id: string, orderType: string): Promise<void> {
    return apiFetch<void>(
      buildUrl(`/v1/online-ordering/orders/${encodeURIComponent(id)}/cancel`, {
        orderType,
      }),
      { method: "PATCH" },
    );
  },

  /**
   * POST /v1/online-ordering/orders/{id}/arrival-confirmation
   * Promise Time: the tracking page's "Has your order arrived?" answer.
   * Idempotent server-side; same credentials as `get` (participant token or
   * customer JWT).
   */
  confirmArrival(id: string): Promise<OrderOutput> {
    const stored = readParticipant();
    const headers: Record<string, string> = {};
    if (stored?.participantToken) {
      headers["X-Participant-Token"] = `Bearer ${stored.participantToken}`;
    }
    return apiFetch<OrderOutput>(
      `/v1/online-ordering/orders/${encodeURIComponent(id)}/arrival-confirmation`,
      { method: "POST", body: {}, headers },
    );
  },
};

/* ──────── Promise Time: quote ──────── */

export const quoteApi = {
  /**
   * POST /v1/online-ordering/quote-eta
   * Quotes the ready / arrival window for the current basket before an order
   * exists. No customer JWT needed (the BFF resolves the tenant from
   * X-API-KEY, which apiFetch attaches to every online-ordering route).
   */
  quote(body: QuoteEtaRequest): Promise<QuoteEtaResponse> {
    return apiFetch<QuoteEtaResponse>("/v1/online-ordering/quote-eta", {
      method: "POST",
      body,
      skipAuth: true,
    });
  },
};

/* ──────── Server-priced checkout: quote-price ──────── */

export const priceQuoteApi = {
  /**
   * POST /v1/online-ordering/quote-price
   * Prices the current basket before an order exists (spec 2026-09-23
   * quote-and-lock). Unlike quote-eta, identity matters here (usage limits
   * on customer-scoped promotions), so the customer JWT is sent when signed
   * in — this call does NOT set `skipAuth`, letting apiFetch attach
   * `Authorization: Bearer <token>` automatically when a token is present.
   * X-API-KEY is still auto-attached by apiFetch for every online-ordering route.
   */
  quote(body: PriceQuoteRequest): Promise<PriceQuote> {
    return apiFetch<PriceQuote>("/v1/online-ordering/quote-price", {
      method: "POST",
      body,
    });
  },
};

/**
 * True when `err` is a 409 PRICE_CHANGED response from order creation (the
 * quote went stale between checkout render and Place order). `quote` may be
 * null — the caller should re-request a quote in that case.
 */
/** The parsed 409 PRICE_CHANGED body — `quote` may be null (re-request a fresh one). */
export type PriceChangedBody = { code: "PRICE_CHANGED"; quote: PriceQuote | null };

function priceChangedBody(err: unknown): PriceChangedBody | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as Record<string, unknown> | null;
  if (!body || typeof body !== "object") return null;
  const detail = body.detail;
  const target = detail && typeof detail === "object" ? (detail as Record<string, unknown>) : body;
  return target.code === "PRICE_CHANGED" ? (target as unknown as PriceChangedBody) : null;
}

export function isPriceChanged(err: unknown): err is ApiError & { priceChanged: PriceChangedBody } {
  return priceChangedBody(err) !== null;
}

/** Reads the parsed PRICE_CHANGED body off a 409 `ApiError` (use after `isPriceChanged` narrows). */
export function priceChangedQuote(err: unknown): PriceQuote | null {
  return priceChangedBody(err)?.quote ?? null;
}

/** 422 LOYALTY_REDEMPTION_INVALID from quote-price: the requested redemption isn't allowed on this cart. */
export function isLoyaltyRedemptionInvalid(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 422) return false;
  const body = err.body as Record<string, unknown> | null;
  if (!body || typeof body !== "object") return false;
  const detail = body.detail;
  const target = detail && typeof detail === "object" ? (detail as Record<string, unknown>) : body;
  return target.code === "LOYALTY_REDEMPTION_INVALID";
}

/* ──────── Availability ──────── */

export const availabilityApi = {
  /**
   * GET /v1/online-ordering/availability/blocked-items
   * v1's `availability.service.ts` exposes a single endpoint that returns
   * blocked items by branch (+ optional channel). The `when` param requested
   * by v2 doesn't exist server-side today.
   *
   * TODO: add a true "is the branch open at <when>" endpoint when available.
   */
  check(
    branchId: string,
    opts: { channelId?: string; when?: string } = {},
  ): Promise<AvailabilityBlockResponse[]> {
    return apiFetch<AvailabilityBlockResponse[]>(
      buildUrl("/v1/online-ordering/availability/blocked-items", {
        branch_id: branchId,
        channelId: opts.channelId,
        // Sent as a hint; current backend ignores it.
        at: opts.when,
      }),
    );
  },
};

/* ──────── Dine-in tables ──────── */

export type TableDetailsResponse = {
  tableId?: string | null;
  id?: string | null;
  tableNumber?: string | null;
  tableName?: string | null;
  name?: string | null;
  status?: string | null;
  floorId?: string | null;
  [key: string]: unknown;
};

export type TableActiveOrderResult = {
  hasActiveOrder: boolean;
  order?: OrderOutput;
};

export const tableApi = {
  /**
   * GET /v1/online-ordering/branches/{branchId}/tables/{tableId}
   * Mirrors v1's `TableContextResolver` validation call.
   */
  getDetails(branchId: string, tableId: string): Promise<TableDetailsResponse> {
    return apiFetch<TableDetailsResponse>(
      `/v1/online-ordering/branches/${encodeURIComponent(branchId)}/tables/${encodeURIComponent(tableId)}`,
    );
  },

  /**
   * GET /v1/online-ordering/branches/{branchId}/tables/{tableId}/active-order
   * Used when a customer scans a QR code for a table with an existing open
   * dine-in order.
   */
  async getActiveOrder(
    branchId: string,
    tableId: string,
  ): Promise<TableActiveOrderResult> {
    try {
      const order = await apiFetch<OrderOutput>(
        `/v1/online-ordering/branches/${encodeURIComponent(branchId)}/tables/${encodeURIComponent(tableId)}/active-order`,
      );
      return { hasActiveOrder: true, order };
    } catch (err: unknown) {
      if (
        err &&
        typeof err === "object" &&
        "status" in err &&
        (err as { status?: number }).status === 404
      ) {
        return { hasActiveOrder: false };
      }
      throw err;
    }
  },
};

/* ──────── Public branch discovery ──────── */

export const branchesApi = {
  /**
   * GET /v1/online-ordering/branches/nearest
   * Mirrors v1's `HomePageService.findAndSetNearestBranch` lookup.
   */
  findNearest(
    lat: number,
    lng: number,
    menuLinkId: string,
    opts: {
      limit?: number;
      radiusKm?: number;
      serviceType?: "delivery" | "pickup" | string;
    } = {},
  ): Promise<NearestBranchResponse> {
    return apiFetch<NearestBranchResponse>(
      buildUrl("/v1/online-ordering/branches/nearest", {
        lat,
        lng,
        menuLinkId,
        limit: opts.limit,
        radiusKm: opts.radiusKm,
        serviceType: opts.serviceType,
      }),
      { skipAuth: true },
    );
  },
};

/* ──────── Surcharges (Task 5) ──────── */

export type SurchargesResponse = {
  definitions: SurchargeDefinition[];
  taxesById: Record<string, TaxRef>;
};

export const surchargesApi = {
  /**
   * GET /v1/online-ordering/surcharges?branchId=...
   *
   * Returns raw ACTIVE surcharge definitions for the tenant (BFF filters
   * isActive server-side; `branchId` is passthrough-only — schedule/branch/
   * order-type/source matching happens client-side at cart/checkout time
   * via `computeAppliedSurcharges`, since it depends on order time).
   * Response shape: `{ data: SurchargeDefinition[], taxesById: { [taxId]: TaxRef } }`.
   * `taxesById` resolves each surcharge's `taxIds` entries to the tax's
   * rate/isIncludedInPrice so surcharge tax can be computed client-side
   * (see `computeAppliedSurcharges` in `@/lib/surcharges`); older BFF
   * deployments that predate this field simply omit it, so we default to `{}`.
   */
  async list(branchId?: string): Promise<SurchargesResponse> {
    const res = await apiFetch<{ data?: SurchargeDefinition[]; taxesById?: Record<string, TaxRef> }>(
      buildUrl("/v1/online-ordering/surcharges", { branchId }),
      { skipAuth: true },
    );
    return { definitions: res?.data ?? [], taxesById: res?.taxesById ?? {} };
  },
};

/* ──────── Promotions ──────── */

export const promotionsApi = {
  /** GET /v1/online-ordering/branches/{branchId}/promotions/active */
  list(
    branchId: string,
    opts: {
      orderType?: string;
      orderSource?: string;
      // Customer identity for per-customer usage limits. The service EXCLUDES a
      // promo this customer has exhausted (maxUsagePerCustomer), so an offer the
      // cart can no longer earn never reaches the promotion engine. `customerId`
      // is the reliable key — a phone alone does not match across the national
      // and international spellings of the same number. Anonymous (neither sent)
      // fails open; order creation is the backstop there.
      customerId?: string;
      customerPhone?: string;
      // Delivery/customer location. Required for geofenced promos to apply —
      // the service fails them closed when absent and excludes out-of-range ones.
      lat?: number;
      lng?: number;
    } = {},
  ): Promise<Promotion[]> {
    return apiFetch<PromotionListResponse>(
      buildUrl(
        `/v1/online-ordering/branches/${encodeURIComponent(branchId)}/promotions/active`,
        {
          orderType: opts.orderType,
          orderSource: opts.orderSource,
          customerId: opts.customerId,
          customerPhone: opts.customerPhone,
          lat: opts.lat,
          lng: opts.lng,
        },
      ),
    ).then((r) => r.promotions ?? []);
  },

  /**
   * Client-side promo code validation.
   *
   * The online-ordering service does not expose a dedicated `validate-code`
   * endpoint (v1's `publicPromotions.service.ts` only has the `active` list),
   * so we fetch the active promotions for the branch and match the supplied
   * `code` against `Promotion.promoCode` (case-insensitive). Returns the
   * matched promotion or `null` if none qualifies.
   */
  async validateCode(
    branchId: string,
    code: string,
    opts: {
      orderType?: string;
      orderSource?: string;
      customerId?: string;
      customerPhone?: string;
    } = {},
  ): Promise<Promotion | null> {
    const trimmed = code.trim();
    if (!trimmed) return null;
    // The list already excludes promos this customer has exhausted, so a code
    // for a spent promo resolves to null and the UI reports it as invalid.
    const list = await promotionsApi.list(branchId, opts);
    const now = new Date();
    const lower = trimmed.toLowerCase();
    const match = list.find((p) => {
      if (!p.promoCode || p.promoCode.toLowerCase() !== lower) return false;
      // Full validity window: dates + weekday + time-of-day (happy hour).
      return isPromotionWithinWindow(p.validity, now);
    });
    return match ?? null;
  },
};

/**
 * Adapt the legacy OO `PromotionReward` (flat shape: `type`, `discountType`,
 * `discountValue`, `discountCap`, `discountTarget`, `discountTargetItemIds`) to
 * the new contract `Reward` union the ported engine understands. Covers the
 * cart-discount kinds OO serves today (`order_discount` / `item_discount`);
 * returns `null` for rewards that are not cart discounts (`free_item` /
 * `free_delivery` / bundle / loyalty / etc.) — those produce 0 discount on this
 * surface, exactly as before.
 *
 * NOTE (asymmetry — load-bearing): the engine's CLOSED-UNION fail-loud
 * (`UnknownRewardError`) applies to the NEW model. Here we translate FROM the
 * OLD model, so an untranslatable reward is a KNOWN "not a cart discount", not
 * an unknown future kind — we return `null` (→ 0 discount), we do NOT throw.
 *
 * This adapter is a temporary bridge until online_ordering_service regenerates
 * its OpenAPI types to the new condition/window/reward model (backend
 * follow-up); regenerating kills this adapter and the `as unknown as OrderInput`
 * casts at the checkout call sites.
 */
function toContractReward(
  reward: PromotionReward | null | undefined,
): Reward | null {
  if (!reward) return null;
  // Contract 1.3.0 rewards the legacy OpenAPI model can't describe yet are
  // passed through in their contract shape (`kind` or `type` discriminator).
  const categoryBundle = asCategoryBundle(reward);
  if (categoryBundle) return categoryBundle;
  if (!reward.discountType || reward.discountValue == null) {
    return null;
  }
  // Legacy `absolute` maps onto the contract's `fixed` absolute discount type.
  const discountType =
    reward.discountType === "percentage" ? "percentage" : "fixed";
  const cap =
    reward.discountCap != null && reward.discountCap > 0
      ? reward.discountCap
      : null;

  if (reward.type === "item_discount") {
    const extra = reward as { maxUnits?: number | null; unitSelection?: UnitSelection | null };
    return {
      kind: "item_discount",
      discountType,
      value: reward.discountValue,
      cap,
      target: (reward.discountTarget as DiscountTarget) ?? "order_total",
      targetItemIds: reward.discountTargetItemIds ?? null,
      maxUnits: extra.maxUnits ?? null,
      unitSelection: extra.unitSelection ?? "lowest_price",
    };
  }
  // Default (order_discount, or any other discount-bearing reward) →
  // order-level discount.
  return {
    kind: "order_discount",
    discountType,
    value: reward.discountValue,
    cap,
  };
}

/** The reward as a contract `category_bundle`, or null when it isn't one. */
export function asCategoryBundle(
  reward: PromotionReward | Reward | null | undefined,
): CategoryBundle | null {
  if (!reward) return null;
  const raw = reward as Partial<CategoryBundle> & { type?: string };
  const kind = raw.kind ?? raw.type;
  if (kind !== "category_bundle" || raw.bundlePrice == null || !raw.groups?.length) {
    return null;
  }
  return {
    kind: "category_bundle",
    bundlePrice: raw.bundlePrice,
    groups: raw.groups,
    maxApplications: raw.maxApplications ?? null,
  };
}

/**
 * Build the engine `CalcContext` from the screen-side cart. `lineSubtotalAmount`
 * is `price * qty` (the OO cart has no separate line subtotal). `subtotal` is
 * the order/items subtotal the screen already computed. Each line carries its
 * menu `categoryId` (from `menuItems`) so category-targeted rewards
 * (`category_bundle`) can match.
 */
export function cartToCalcContext(
  subtotal: number,
  cart: Array<{ itemId: string; price: number; qty: number; categoryId?: string | null }>,
  menuItems: Array<{ id: string; categoryId?: string }> = [],
): CalcContext {
  const categoryOf = new Map(menuItems.map((m) => [m.id, m.categoryId]));
  return {
    orderLines: cart.map((line, i) => ({
      lineId: `l${i}`,
      itemId: line.itemId,
      quantity: line.qty,
      unitPrice: line.price,
      lineSubtotalAmount: line.price * line.qty,
      categoryId: line.categoryId ?? categoryOf.get(line.itemId) ?? null,
    })),
    itemsSubtotalAmount: subtotal,
    orderSubtotalAmount: subtotal,
  };
}

/**
 * Compute the discount amount a promotion applies to a given subtotal.
 *
 * Eligibility gating (`criteria.minOrderValue` + `criteria.requiredItemCriteria`)
 * stays screen-side here; once eligible, the discount MATH is delegated to the
 * ported `promotion-engine` (the authoritative `promotion_calc_contract`),
 * killing the drift the old inline math carried. Returns 0 for non-discount
 * reward types (e.g. free items / free delivery).
 *
 * FAIL LOUD: the engine throws `UnknownRewardError` on an unknown reward kind —
 * we deliberately do NOT swallow it, so a forward-incompatible reward surfaces
 * loudly instead of silently zeroing the discount (which would undercharge).
 * The legacy→contract `toContractReward` bridge returns `null` for known
 * non-cart-discount rewards, so those still 0 without reaching the throw.
 */
export function computePromotionDiscount(
  promotion: Promotion,
  subtotal: number,
  cart: Array<{ itemId: string; price: number; qty: number }> = [],
  menuItems: Array<{ id: string; categoryId?: string }> = [],
): number {
  const reward = promotion.reward;
  // Translate first: a contract-shaped reward (e.g. category_bundle) has no
  // legacy discountType/discountValue but is still a cart discount.
  const contractReward = toContractReward(reward);
  if (!contractReward) return 0; // non-discount / untranslatable old-shape reward

  // Eligibility gate: enforce `criteria.minOrderValue`
  const minOrder = promotion.criteria?.minOrderValue ?? 0;
  if (minOrder > 0 && subtotal < minOrder) {
    return 0;
  }

  // Eligibility gate: enforce `criteria.requiredItemCriteria` (e.g. "buy 2 items"),
  // honouring matchScope (any total vs each-item) to match the backend engine.
  if (!meetsRequiredItemCriteria(promotion.criteria?.requiredItemCriteria, cart, menuItems)) {
    return 0;
  }

  // Eligible — compute the reward amount via the ported contract engine.
  // Only the order-level total is used on this surface; lines are never
  // re-priced client-side (if they ever are, apply the engine's
  // `lineAllocations` exactly — never re-prorate `discountAmount`).
  const ctx = cartToCalcContext(subtotal, cart, menuItems);
  let amount = evaluateReward(contractReward, ctx).discountAmount;
  if (amount > subtotal) amount = subtotal;
  return amount > 0 ? amount : 0;
}

/**
 * Program-neutral loyalty earn signals an applied promotion contributes to the
 * order (`{ earnMultiplier?, loyaltyCredit? }`). Delegates to the engine: only
 * `earn_multiplier` / `loyalty_credit` rewards yield non-empty signals; today's
 * OO promotions (order/item discounts) yield `{}`, so this is forward-compatible
 * without changing current behaviour. The client NEVER resolves points vs
 * cashback — loyalty does (Plan 2). Returns `{}` for non-signal / untranslatable
 * rewards.
 */
export function promotionLoyaltySignals(promotion: Promotion): LoyaltySignals {
  const r = toContractReward(promotion.reward);
  if (!r) return {};
  return evaluateReward(r, {
    orderLines: [],
    itemsSubtotalAmount: 0,
    orderSubtotalAmount: 0,
  }).loyaltySignals;
}

/** Same rule as the POS till (the price authority): only autoApply === true
 *  applies by itself, with or without a code. Anything else needs its code. */
export function isAutoAppliedLikeTill(p: Promotion): boolean {
  return p.autoApply === true;
}

/**
 * Pick the best auto-applied promotion against a given subtotal. Mirrors
 * (a much simpler) version of v1's `PromotionsService.evaluateCartPromotions`:
 * for each promotion that is active + auto-apply + within validity + has no
 * promo-code gate, compute its discount and return the one with the highest
 * effective discount. Returns `null` if none qualifies.
 *
 * Used by the cart to surface a "saving X" line without the user pasting a
 * code, matching v1's home-page auto-apply behaviour.
 */
export function pickAutoAppliedPromotion(
  promotions: Promotion[],
  subtotal: number,
  now: Date = new Date(),
  cart: Array<{ itemId: string; price: number; qty: number }> = [],
  menuItems: Array<{ id: string; categoryId?: string }> = [],
): { promotion: Promotion; discount: number } | null {
  let best: { promotion: Promotion; discount: number } | null = null;
  for (const p of promotions) {
    if (!isAutoAppliedLikeTill(p)) continue;
    // Full validity window: dates + weekday + time-of-day (happy hour).
    if (!isPromotionWithinWindow(p.validity, now)) continue;
    const discount = computePromotionDiscount(p, subtotal, cart, menuItems);
    if (discount <= 0) continue;
    if (!best || discount > best.discount) best = { promotion: p, discount };
  }
  return best;
}

/* ──────── Loyalty ──────── */

/**
 * Shape of the loyalty balance response from the online-ordering service.
 * Mirrors v1's `LoyaltyService.getCustomerLoyaltyBalance` which returns an
 * untyped `{ [key: string]: any }`. We keep the typing soft-but-useful here
 * — the server-side schema is loyalty-program-dependent and not yet pinned
 * down in the OpenAPI spec.
 *
 * TODO: tighten once the loyalty service publishes a stable schema.
 */
export type LoyaltyBalanceResponse = {
  customerId?: string;
  programId?: string | null;
  programType?: string | null;
  points?: number | null;
  pointsBalance?: number | null;
  availablePoints?: number | null;
  totalPoints?: number | null;
  cashback?: number | null;
  cashbackBalance?: number | null;
  availableCashback?: number | null;
  pointsExpiringSoon?: number | null;
  tier?: string | null;
  tierName?: string | null;
  nextTier?: { name?: string | null; pointsAway?: number | null } | null;
  // Some implementations bundle redeemable rewards into the balance payload.
  rewards?: LoyaltyRewardResponse[] | null;
  [key: string]: unknown;
};

export type LoyaltyRewardResponse = {
  id: string;
  name?: string | null;
  description?: string | null;
  pointsCost?: number | null;
  [key: string]: unknown;
};

export type LoyaltyTransactionResponse = {
  id?: string | null;
  type?: string | null;
  transactionType?: string | null;
  eventType?: string | null;
  description?: string | null;
  reason?: string | null;
  status?: string | null;
  pointsChange?: number | null;
  points?: number | null;
  cashbackChange?: number | null;
  cashback?: number | null;
  amount?: number | null;
  orderId?: string | null;
  orderNumber?: string | null;
  createdAt?: string | null;
  timestamp?: string | null;
  date?: string | null;
  [key: string]: unknown;
};

/**
 * Shape of a single redemption option returned by `/redemption-options`.
 * Mirrors v1's `RedemptionData` type as consumed by the checkout page.
 */
export type RedemptionOption = {
  type: "points" | "cashback";
  /** Points the user has available to spend on this order. */
  maxRedeemable?: number | null;
  /** Currency value the maxRedeemable converts to (what the user actually saves). */
  redeemableValue?: number | null;
  /** The loyalty program backing this option. */
  programId?: string | null;
  programType?: string | null;
  pointsValue?: number | null;
  currency?: string | null;
  /** Some backends include extra metadata; keep it pass-through. */
  [key: string]: unknown;
};

export type RedemptionOptionsResponse = {
  available_options?: RedemptionOption[];
  availableOptions?: RedemptionOption[];
  [key: string]: unknown;
};

export type HoldPointsPayload = {
  customer_id: string;
  points_to_hold: number;
  cashback_to_hold: number;
  related_order_id: string | null;
};

/** Loyalty earn rule that sets the rate for an order (e.g. first-order welcome bonus). */
export type EarnRuleSummary = {
  id: string;
  name: { en: string; ar?: string | null };
  rate: number;
  maxEarnPerOrder?: number | null;
};

/** The program's order-#1 earn rule, advertised to signed-out visitors. */
export type WelcomeOffer = {
  ruleId: string;
  rate: number;
  maxEarnPerOrder?: number | null;
};

export type EarningsEstimate = {
  estimatedPoints: number;
  estimatedCashback: number;
  currency?: string;
  programType?: string | null;
  earnRule?: EarnRuleSummary | null;
  orderNumber?: number | null;
  welcomeOffer?: WelcomeOffer | null;
  [key: string]: unknown;
};

export const loyaltyApi = {
  /**
   * POST /v1/online-ordering/earnings/estimate
   *
   * What the signed-in customer will earn on this draft order. Applies the
   * program's earn rules exactly like real accrual, so a first-order welcome
   * rate shows up here before checkout.
   */
  estimateEarnings(orderDraft: unknown): Promise<EarningsEstimate> {
    return apiFetch<EarningsEstimate>("/v1/online-ordering/earnings/estimate", {
      method: "POST",
      body: orderDraft,
    });
  },

  /** GET /v1/online-ordering/earnings/welcome-offer — no customer required. */
  welcomeOffer(): Promise<{ welcomeOffer: WelcomeOffer | null; programType?: string | null }> {
    return apiFetch<{ welcomeOffer: WelcomeOffer | null; programType?: string | null }>(
      "/v1/online-ordering/earnings/welcome-offer",
    );
  },

  /**
   * GET /v1/online-ordering/customers/{customerId}/balance
   * Mirrors v1's `LoyaltyService.getCustomerLoyaltyBalance`.
   */
  getBalance(customerId: string): Promise<LoyaltyBalanceResponse> {
    return apiFetch<LoyaltyBalanceResponse>(
      `/v1/online-ordering/customers/${encodeURIComponent(customerId)}/balance`,
    );
  },

  /**
   * POST /v1/online-ordering/redemption-options
   *
   * Asks the backend "given this draft order, what loyalty rewards can the
   * customer redeem against it?" The response contains a list of options the
   * checkout UI surfaces as a single "Use rewards" toggle (mirrors v1's
   * `OrderService.redemptionOptions`).
   */
  async redemptionOptions(orderDraft: unknown): Promise<RedemptionOption[]> {
    const res = await apiFetch<RedemptionOptionsResponse>(
      "/v1/online-ordering/redemption-options",
      { method: "POST", body: orderDraft },
    );
    return res?.available_options ?? res?.availableOptions ?? [];
  },

  /**
   * POST /v1/online-ordering/loyalty-actions/hold-points
   *
   * Locks the redeemed points/cashback against the just-created order so they
   * can't be double-spent before the order completes. Called *after* order
   * creation so `related_order_id` is set; mirrors v1's checkout flow.
   */
  holdPoints(payload: HoldPointsPayload): Promise<unknown> {
    return apiFetch<unknown>(
      "/v1/online-ordering/loyalty-actions/hold-points",
      { method: "POST", body: payload },
    );
  },

  /**
   * GET /v1/online-ordering/customers/{customerId}/rewards
   *
   * NOTE: v1's loyalty SDK does not expose a dedicated "list rewards"
   * endpoint — the balance payload is the canonical place rewards appear,
   * and redemption options come from `POST /redemption-options`.
   * We attempt the optimistic path here and fall back to an empty list on
   * 404 so callers always get a usable shape.
   *
   * TODO: switch to a real endpoint once the loyalty service exposes one.
   */
  async getRewards(customerId: string): Promise<LoyaltyRewardResponse[]> {
    try {
      const res = await apiFetch<
        LoyaltyRewardResponse[] | { rewards?: LoyaltyRewardResponse[] }
      >(
        `/v1/online-ordering/customers/${encodeURIComponent(customerId)}/rewards`,
      );
      if (Array.isArray(res)) return res;
      if (res && Array.isArray(res.rewards)) return res.rewards;
      return [];
    } catch (err: unknown) {
      // 404 → endpoint not implemented yet. Anything else: rethrow.
      if (
        err &&
        typeof err === "object" &&
        "status" in err &&
        (err as { status?: number }).status === 404
      ) {
        return [];
      }
      throw err;
    }
  },

  /**
   * GET /v1/online-ordering/customers/{customerId}/transactions
   * Mirrors v1's `getCustomerTransactions...` generated SDK method.
   */
  async getTransactions(
    customerId: string,
  ): Promise<LoyaltyTransactionResponse[]> {
    const res = await apiFetch<
      | LoyaltyTransactionResponse[]
      | {
          data?: LoyaltyTransactionResponse[];
          transactions?: LoyaltyTransactionResponse[];
          items?: LoyaltyTransactionResponse[];
        }
    >(
      `/v1/online-ordering/customers/${encodeURIComponent(customerId)}/transactions`,
    );
    if (Array.isArray(res)) return res;
    if (res && Array.isArray(res.data)) return res.data;
    if (res && Array.isArray(res.transactions)) return res.transactions;
    if (res && Array.isArray(res.items)) return res.items;
    return [];
  },
};

/* ──────── Payments ──────── */

export type SavedCard = {
  id: string;
  providerKey?: string;
  last4: string;
  brand: string;
  // Paymob never exposes expiry to merchants. Keep these optional so the
  // UI can hide the expiry line for Paymob-issued tokens.
  expiryMonth?: number | null;
  expiryYear?: number | null;
  isDefault: boolean;
  // False when the card's provider has no integration able to settle a
  // stored-token charge (e.g. Paymob without MOTO). Absent on older
  // payment-service builds — treat only an explicit false as unchargeable.
  chargeable?: boolean;
};

export type SavedCardsResponse = {
  cards?: SavedCard[];
  savedCardPaymentsAvailable?: boolean;
};

export type FawryVerifyResponse = {
  status: string;
  orderId: string;
  fawryStatus?: string;
  message?: string;
};

export const paymentApi = {
  /** POST /v1/online-ordering/payments/intent */
  createIntent(
    req: PaymentIntentCreatePayload,
  ): Promise<PaymentIntentResponse> {
    return apiFetch<PaymentIntentResponse>(
      "/v1/online-ordering/payments/intent",
      { method: "POST", body: req },
    );
  },

  /**
   * POST /v1/online-ordering/orders/{orderId}/confirm-payment
   *
   * Frontend calls this after the provider SDK reports success — the backend
   * verifies the payment with the provider (or trusts the webhook that already
   * arrived) and transitions the order out of `pending_payment`.
   */
  confirmPayment(
    orderId: string,
    body: ConfirmPaymentRequest,
    idempotencyKey?: string,
  ): Promise<OrderOutput> {
    const headers: Record<string, string> = {};
    // Confirm-payment is the most expensive endpoint to call twice (a duplicate
    // can lead to a double-charge). Always send an Idempotency-Key — falling
    // back to the order id + provider ref if the caller did not supply one.
    const key =
      idempotencyKey ??
      [
        orderId,
        body.intentId ?? body.transactionRef ?? body.providerKey ?? "confirm",
      ]
        .filter(Boolean)
        .join(":");
    headers["Idempotency-Key"] = key;
    return apiFetch<OrderOutput>(
      `/v1/online-ordering/orders/${encodeURIComponent(orderId)}/confirm-payment`,
      { method: "POST", body, headers },
    );
  },

  /** POST /v1/online-ordering/payments/online */
  processOnline(req: ChargeCreate): Promise<ChargeProcessResponse> {
    return apiFetch<ChargeProcessResponse>(
      "/v1/online-ordering/payments/online",
      { method: "POST", body: req },
    );
  },

  /** GET /v1/online-ordering/payments/saved-cards */
  listSavedCards(): Promise<SavedCard[]> {
    return apiFetch<SavedCardsResponse>(
      "/v1/online-ordering/payments/saved-cards",
    ).then((r) => r?.cards ?? []);
  },

  /** DELETE /v1/online-ordering/payments/saved-cards/{cardId} */
  deleteSavedCard(cardId: string): Promise<void> {
    return apiFetch<void>(
      `/v1/online-ordering/payments/saved-cards/${encodeURIComponent(cardId)}`,
      { method: "DELETE" },
    );
  },

  /** POST /v1/online-ordering/payments/fawry-verify */
  fawryVerify(
    orderId: string,
    merchantRefNum?: string,
  ): Promise<FawryVerifyResponse> {
    return apiFetch<FawryVerifyResponse>(
      "/v1/online-ordering/payments/fawry-verify",
      { method: "POST", body: { orderId, merchantRefNum } },
    );
  },
};

/* ──────── Public configuration ──────── */

export const configApi = {
  /** GET /v1/online-ordering/config/{urlPath} */
  get(urlPath: string): Promise<OnlineOrderingConfig> {
    return apiFetch<OnlineOrderingConfig>(
      `/v1/online-ordering/config/${encodeURIComponent(urlPath)}`,
      { skipAuth: true },
    );
  },
};

/* ──────── Loyalty expansion (Block 5) ────────
 *
 * Customer-facing surfaces for tier benefits, gamification (challenges /
 * streaks / badges), referrals, and wallet passes. These talk to the
 * loyalty service directly through the gateway under `/v1/loyalty/...`
 * (contract: docs/superpowers/plans/loyalty-marketing-revamp/
 * loyalty-expansion/EXECUTION.md — sections L1/L3/L4).
 *
 * The backend ships in parallel: every wrapper tolerates 404 (endpoint not
 * deployed yet) and the gamification endpoints return `enabled: false`
 * sections that the UI uses to hide surfaces entirely.
 */

import type { LocalizedString } from "@/api/online-ordering-service/model";

export type TierBenefitType =
  | "free_delivery"
  | "priority_queue"
  | "vip_table"
  | "early_access_menu"
  | "birthday_free_item"
  | "points_multiplier";

export type ResolvedBenefit = {
  type: TierBenefitType | string;
  params?: Record<string, unknown> | null;
  [key: string]: unknown;
};

export type TierGrace = {
  pendingTierId?: string | null;
  pendingTierName?: string | LocalizedString | null;
  graceUntil?: string | null;
  [key: string]: unknown;
};

export type BenefitsResolveResponse = {
  tierId: string | null;
  tierName?: string | LocalizedString | null;
  programId?: string | null;
  benefits: ResolvedBenefit[];
  /** Present while a downgrade is pending (ANNUAL_SPEND basis). */
  tierGrace?: TierGrace | null;
  [key: string]: unknown;
};

/** One rung of the tenant's loyalty tier ladder (real, tenant-defined). */
export type TierLadderEntry = {
  tierId: string;
  name: string | LocalizedString;
  minThreshold?: number;
  thresholdBasis?: string | null;
  accrualMultiplier?: number;
  benefitsDescription?: string | LocalizedString | null;
  benefits: ResolvedBenefit[];
};

export type TierLadderResponse = {
  programId?: string | null;
  programName?: string | LocalizedString | null;
  tiers: TierLadderEntry[];
};

/** Reward union used by challenges / streak milestones / referrals. */
export type LoyaltyReward2 =
  | { type: "points"; amount: number }
  | { type: "promo_code"; promotionId: string }
  | { type: "free_item"; itemId: string; expiresAt?: string | null }
  | { type?: string; [key: string]: unknown };

export type CustomerChallenge = {
  id: string;
  challengeId?: string | null;
  name?: string | LocalizedString | null;
  description?: string | LocalizedString | null;
  criteria?: {
    metric?: "order_count" | "total_spend" | string;
    target?: number;
    windowStart?: string | null;
    windowEnd?: string | null;
    branchIds?: string[] | null;
  } | null;
  reward?: LoyaltyReward2 | null;
  status?: string | null;
  progress?: number | null;
  target?: number | null;
  completedAt?: string | null;
  rewardIssued?: boolean | null;
  [key: string]: unknown;
};

export type StreakInfo = {
  count?: number | null;
  lastPeriodKey?: string | null;
  [key: string]: unknown;
};

export type StreakMilestone = {
  type?: string;
  count?: number;
  reward?: LoyaltyReward2 | null;
  [key: string]: unknown;
};

export type CustomerStreaks = {
  enabled?: boolean;
  weeklyVisit?: StreakInfo | null;
  monthlyOrder?: StreakInfo | null;
  milestones?: StreakMilestone[] | null;
  [key: string]: unknown;
};

export type CustomerChallengesResponse = {
  enabled?: boolean;
  challengesEnabled?: boolean;
  streaksEnabled?: boolean;
  challenges?: CustomerChallenge[] | null;
  active?: CustomerChallenge[] | null;
  completed?: CustomerChallenge[] | null;
  history?: CustomerChallenge[] | null;
  streaks?: CustomerStreaks | null;
  [key: string]: unknown;
};

export type CustomerBadge = {
  id?: string | null;
  badgeId?: string | null;
  name?: string | LocalizedString | null;
  description?: string | LocalizedString | null;
  icon?: string | null;
  rule?: { metric?: string; threshold?: number } | null;
  status?: string | null;
  earned?: boolean | null;
  awardedAt?: string | null;
  /** 0..1 or 0..100 depending on backend; the UI normalizes. */
  progress?: number | null;
  progressPct?: number | null;
  /** Percentage of the tenant's customers who hold this badge. */
  rarityPct?: number | null;
  hidden?: boolean | null;
  locked?: boolean | null;
  [key: string]: unknown;
};

export type CustomerBadgesResponse = {
  enabled?: boolean;
  badgesEnabled?: boolean;
  badges?: CustomerBadge[] | null;
  earned?: CustomerBadge[] | null;
  inProgress?: CustomerBadge[] | null;
  locked?: CustomerBadge[] | null;
  [key: string]: unknown;
};

export type ReferralHistoryEntry = {
  refereeName?: string | null;
  name?: string | null;
  joinedAt?: string | null;
  createdAt?: string | null;
  status?: string | null; // pending | attributed | rewarded | expired | flagged_self_referral
  reward?: LoyaltyReward2 | null;
  [key: string]: unknown;
};

export type ReferralResponse = {
  enabled?: boolean;
  code: string;
  link?: string | null;
  referrerReward?: LoyaltyReward2 | null;
  refereeReward?: LoyaltyReward2 | null;
  stats?: {
    invited?: number;
    attributed?: number;
    rewardsEarned?: number;
    [key: string]: unknown;
  } | null;
  history?: ReferralHistoryEntry[] | null;
  [key: string]: unknown;
};

export type GoogleWalletPassResponse = {
  saveUrl: string;
  [key: string]: unknown;
};

/** Typed 503 body when pass signing certs are not provisioned yet. */
export const PASS_NOT_CONFIGURED = "PASS_NOT_CONFIGURED";

export function isPassNotConfigured(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.status !== 503) return false;
  const body = err.body as { code?: string; detail?: { code?: string } } | null;
  return (
    body?.code === PASS_NOT_CONFIGURED ||
    body?.detail?.code === PASS_NOT_CONFIGURED ||
    // Be lenient: any 503 from the pass endpoints means "not ready yet".
    true
  );
}

// NOTE: all loyalty/offer calls go through the `/v1/online-ordering/*` customer
// edge (online_ordering_service), NOT `/v1/loyalty/*` or `/v1/promotions/*`
// directly. The customer OTP token is not a project Firebase token, so the
// gateway rejects it (401) on the operator routes; the online-ordering edge is
// open at the gateway, validates the customer + api-key, and proxies to the
// backends with internal service auth. `/v1/online-ordering/*` paths also get
// X-API-KEY auto-attached by apiFetch.
export const benefitsApi = {
  /**
   * GET /v1/online-ordering/customers/{id}/benefits
   * Returns the customer's current tier + enabled benefits. The backend NEVER
   * 5xxs for a missing loyalty account — it returns `tierId: null` with an
   * empty benefits list instead.
   */
  resolve(
    customerId: string,
    _context: { action?: string; branchId?: string } = {},
  ): Promise<BenefitsResolveResponse> {
    return apiFetch<BenefitsResolveResponse>(
      `/v1/online-ordering/customers/${encodeURIComponent(customerId)}/benefits`,
    );
  },

  /**
   * GET /v1/online-ordering/loyalty/tiers
   * The tenant's own tier ladder (ordered by threshold). Used by the no-tier
   * explainer for brand-new customers so it shows real tenant tiers, not a
   * hardcoded list. Returns an empty `tiers` array (not an error) when the
   * tenant has no active program — callers then show a neutral message.
   */
  listTiers(): Promise<TierLadderResponse> {
    return apiFetch<TierLadderResponse>("/v1/online-ordering/loyalty/tiers");
  },
};

export const gamificationApi = {
  /**
   * GET /v1/loyalty/customers/{cid}/challenges
   * Active challenges + progress + completed history + streaks. Returns
   * `enabled: false` sections when the tenant has gamification off.
   */
  getChallenges(customerId: string): Promise<CustomerChallengesResponse> {
    return apiFetch<CustomerChallengesResponse>(
      `/v1/online-ordering/customers/${encodeURIComponent(customerId)}/challenges`,
    );
  },

  /**
   * GET /v1/loyalty/customers/{cid}/badges
   * Badge catalog + earned / in-progress state for the customer.
   */
  getBadges(customerId: string): Promise<CustomerBadgesResponse> {
    return apiFetch<CustomerBadgesResponse>(
      `/v1/online-ordering/customers/${encodeURIComponent(customerId)}/badges`,
    );
  },
};

export const referralApi = {
  /**
   * GET /v1/loyalty/customers/{cid}/referral
   * The customer's referral code, share link, stats, and history.
   */
  get(customerId: string): Promise<ReferralResponse> {
    return apiFetch<ReferralResponse>(
      `/v1/online-ordering/customers/${encodeURIComponent(customerId)}/referral`,
    );
  },
};

/* ──────── Offers (Block 4 — promotion-engine-upgrades) ────────
 *
 * Customer-facing offer surfaces: the "Your offers" inbox plus the inline
 * next-best-offer cross-sell on Home / Checkout / post-order. These talk to
 * the `discount_promotion_service` through the SAME gateway base the app uses
 * for promotions, under `/v1/promotions/...` (contract:
 * docs/superpowers/plans/loyalty-marketing-revamp/promotion-engine-upgrades/
 * 00-overview.md — sections 7.5/7.6 + "Contracts established by this block").
 *
 * Both wrappers degrade gracefully: the endpoints may 404 until the service is
 * deployed, so every consumer treats failure as "no offers" and renders
 * nothing rather than blocking the screen.
 */

/** Surfaces the next-best-offer ranker is asked about. */
export type OfferSurface =
  | "home"
  | "checkout_crosssell"
  | "post_order"
  | "inbox";

export type OfferBadge = "new" | "redeemed" | "expiring" | null;

/**
 * The canonical offer card returned by both `/offers` and `/offers/next-best`.
 * Bound EXACTLY to the backend contract — do not add fields the service does
 * not send. `score` / `reasons` only appear on next-best results.
 */
export type OfferCard = {
  promotionId: string;
  headline: string;
  description: string;
  /**
   * Reward kind that drives the offer's icon/copy (was `mechanicType`, the
   * deleted mechanic union — Plan 1 renamed the offer dimension to the reward
   * kind). May be a raw string until the backend rename ships.
   */
  rewardKind: RewardKind | string;
  /** Code-based offers carry a redeemable code; auto-applied / category-tied don't. */
  code?: string | null;
  autoApplied: boolean;
  expiresAt?: string | null;
  badge?: OfferBadge;
  /** @deprecated transitional — the deployed backend may still send `mechanicType` pre-rename; read via the normalizers below, do not consume directly. */
  mechanicType?: string;
};

export type NextBestOffer = OfferCard & {
  score: number;
  reasons: string[];
};

export type OffersInboxResponse = {
  active: OfferCard[];
  upcoming: OfferCard[];
  expired: OfferCard[];
};

export type NextBestContext = {
  surface: OfferSurface;
  branchId?: string | null;
  cartCategoryIds?: string[] | null;
};

const EMPTY_INBOX: OffersInboxResponse = {
  active: [],
  upcoming: [],
  expired: [],
};

/**
 * Normalize an offer card from the backend onto the `rewardKind` field. During
 * the rename cutover the deployed discount service may still emit the old
 * `mechanicType`; prefer `rewardKind` and fall back to `mechanicType` so the
 * icon/copy keep resolving either way. Drop once the backend rename ships
 * (OpenAPI regen follow-up).
 */
function normalizeOfferCard<T extends OfferCard>(o: T): T {
  const rewardKind =
    o.rewardKind ?? (o as { mechanicType?: string }).mechanicType ?? "";
  return { ...o, rewardKind };
}

function isNotFound(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    (err.status === 404 || err.status === 501)
  );
}

export const offersApi = {
  /**
   * GET /v1/promotions/promotions/{customerId}/offers
   *
   * Note the doubled `/promotions`: the discount service mounts its api_router at
   * `/v1/promotions` and its routes are declared as `/promotions/...`, and the API
   * gateway forwards the full path (APPEND_PATH_TO_ADDRESS) — so the served path is
   * `/v1/promotions/promotions/customers/{id}/offers`.
   *
   * The customer's categorized offer inbox (active / upcoming / expired).
   * Returns an empty-but-valid shape if the endpoint isn't deployed yet (404)
   * so the inbox renders its empty state instead of an error.
   */
  async getInbox(customerId: string): Promise<OffersInboxResponse> {
    try {
      const res = await apiFetch<any>(
        `/v1/online-ordering/customers/${encodeURIComponent(customerId)}/offers`,
      );
      
      if (res && (res.active || res.upcoming || res.expired)) {
        return {
          active: Array.isArray(res.active) ? res.active.map(normalizeOfferCard) : [],
          upcoming: Array.isArray(res.upcoming) ? res.upcoming.map(normalizeOfferCard) : [],
          expired: Array.isArray(res.expired) ? res.expired.map(normalizeOfferCard) : [],
        };
      }

      const rawOffers = Array.isArray(res?.offers) ? res.offers : [];
      const active: OfferCard[] = [];
      const upcoming: OfferCard[] = [];
      const expired: OfferCard[] = [];
      const now = new Date();

      for (const promo of rawOffers) {
        const headline = promo.name?.en || promo.name?.ar || "";
        const description = promo.description?.en || promo.description?.ar || "";
        const mechanicType = promo.reward?.type || promo.reward?.kind || "unknown";
        const code = promo.promoCode || null;
        const autoApplied = !!promo.autoApply;
        const expiresAt = promo.validity?.endDate || null;
        const startDate = promo.validity?.startDate || null;

        let isExpired = false;
        let isUpcoming = false;

        if (promo.status === "expired") {
          isExpired = true;
        } else if (expiresAt && new Date(expiresAt) < now) {
          isExpired = true;
        } else if (startDate && new Date(startDate) > now) {
          isUpcoming = true;
        } else if (promo.status !== "active") {
          isExpired = true;
        }

        let badge: OfferBadge = null;
        if (!isExpired && !isUpcoming) {
          if (promo.createdAt && now.getTime() - new Date(promo.createdAt).getTime() < 3 * 24 * 60 * 60 * 1000) {
            badge = "new";
          } else if (expiresAt && new Date(expiresAt).getTime() - now.getTime() < 3 * 24 * 60 * 60 * 1000) {
            badge = "expiring";
          }
        }

        const card: OfferCard = {
          promotionId: promo.id,
          headline,
          description,
          rewardKind: mechanicType,
          mechanicType,
          code,
          autoApplied,
          expiresAt,
          badge,
        };

        if (isExpired) {
          expired.push(card);
        } else if (isUpcoming) {
          upcoming.push(card);
        } else {
          active.push(card);
        }
      }

      return { active, upcoming, expired };
    } catch (err) {
      if (isNotFound(err)) return EMPTY_INBOX;
      throw err;
    }
  },

  /**
   * POST /v1/promotions/offers/next-best
   *
   * The heuristic ranker. The backend ALWAYS returns 200 with a (possibly
   * empty) ranked list, capped per surface (home 3 / checkout_crosssell 2 /
   * post_order 1). We still fail soft on transport errors so a ranker outage
   * never blanks the host screen — callers get `[]`.
   */
  async nextBest(
    customerId: string,
    context: NextBestContext,
  ): Promise<NextBestOffer[]> {
    try {
      const res = await apiFetch<any>(
        // doubled `/promotions` — see getInbox note above
        "/v1/online-ordering/offers/next-best",
        { method: "POST", body: { customerId, context } },
      );
      if (Array.isArray(res?.offers) && res.offers.length > 0 && res.offers[0].promotionId) {
        return res.offers.map(normalizeOfferCard);
      }
      
      const rawOffers = Array.isArray(res?.offers) ? res.offers : [];
      return rawOffers.map((promo: any) => {
        const mechanicType = promo.reward?.type || promo.reward?.kind || "unknown";
        return {
          promotionId: promo.id,
          headline: promo.name?.en || promo.name?.ar || "",
          description: promo.description?.en || promo.description?.ar || "",
          rewardKind: mechanicType,
          mechanicType,
          code: promo.promoCode || null,
          autoApplied: !!promo.autoApply,
          expiresAt: promo.validity?.endDate || null,
          badge: null,
          score: promo.score || promo.rankerWeight || 0,
          reasons: [],
        };
      });
    } catch (err) {
      // Ranker is best-effort everywhere it's used. Swallow ALL errors and
      // return an empty list so surfaces fall back to rendering nothing.
      if (!isNotFound(err)) {
        console.warn("[offers] next-best fetch failed", err);
      }
      return [];
    }
  },
};

export const passApi = {
  /**
   * GET /v1/loyalty/customers/{cid}/pass/google
   * Returns a "save to Google Wallet" URL, or 503 PASS_NOT_CONFIGURED
   * until ops provisions the issuer credentials.
   */
  getGooglePass(customerId: string): Promise<GoogleWalletPassResponse> {
    return apiFetch<GoogleWalletPassResponse>(
      `/v1/online-ordering/customers/${encodeURIComponent(customerId)}/pass/google`,
    );
  },

  /**
   * GET /v1/loyalty/customers/{cid}/pass/apple
   * Returns raw `.pkpass` bytes (or 503 PASS_NOT_CONFIGURED). Bypasses
   * `apiFetch` because the response is binary, not JSON.
   */
  async downloadApplePass(customerId: string): Promise<Blob> {
    const path = `/v1/online-ordering/customers/${encodeURIComponent(customerId)}/pass/apple`;
    const headers = new Headers({ Accept: "application/vnd.apple.pkpass" });
    const token = getToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    const res = await fetch(`${API_BASE}${path}`, {
      headers,
      credentials: "include",
    });
    if (!res.ok) {
      let body: unknown;
      const text = await res.text();
      try {
        body = JSON.parse(text);
      } catch {
        body = text;
      }
      throw new ApiError(res.status, body, `GET ${path} failed`);
    }
    return res.blob();
  },
};
